import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Outlet, NavLink, Navigate, useNavigate } from 'react-router-dom';
import api, { clearAdminSession } from '../../services/api';
import './admin.css';

// Eventos que contam como "o admin está usando o painel".
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'wheel', 'scroll', 'touchstart'];

// De quanto em quanto tempo o contador confere a ociosidade. Não precisa ser
// preciso ao segundo, e o intervalo curto evita ficar reagendando timers a cada
// movimento do mouse.
const IDLE_CHECK_INTERVAL_MS = 15 * 1000;

const DEFAULT_IDLE_TIMEOUT_MS = 30 * 60 * 1000;

export default function AdminLayout() {
  const navigate = useNavigate();
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [idleTimeoutMs, setIdleTimeoutMs] = useState(DEFAULT_IDLE_TIMEOUT_MS);

  const lastActivityRef = useRef(Date.now());
  const encerrandoRef = useRef(false);

  const encerrarSessao = useCallback(async (motivo) => {
    if (encerrandoRef.current) return;
    encerrandoRef.current = true;

    try {
      await api.post('/admin/auth/logout');
    } catch {
      /* a sessão pode já ter sido derrubada pelo servidor */
    }
    clearAdminSession();
    navigate(motivo ? `/tocadochefe?motivo=${motivo}` : '/tocadochefe', { replace: true });
  }, [navigate]);

  useEffect(() => {
    let active = true;
    const token = sessionStorage.getItem('adminToken');

    if (!token) {
      setCheckingAuth(false);
      navigate('/tocadochefe', { replace: true });
      return () => { active = false; };
    }

    api.get('/admin/auth/validate')
      .then(({ data }) => {
        if (!active) return;
        if (data?.idleTimeoutMs > 0) setIdleTimeoutMs(data.idleTimeoutMs);
        lastActivityRef.current = Date.now();
        setAuthorized(true);
      })
      .catch(() => {
        clearAdminSession();
        if (active) navigate('/tocadochefe', { replace: true });
      })
      .finally(() => {
        if (active) setCheckingAuth(false);
      });

    return () => { active = false; };
  }, [navigate]);

  // Corte por ociosidade. O servidor também recusa renovar uma sessão parada
  // (ver adminAuthController), então desativar isto no navegador não dá acesso.
  useEffect(() => {
    if (!authorized) return undefined;

    const registrarAtividade = () => { lastActivityRef.current = Date.now(); };
    ACTIVITY_EVENTS.forEach(evento =>
      window.addEventListener(evento, registrarAtividade, { passive: true })
    );

    const intervalo = setInterval(() => {
      if (Date.now() - lastActivityRef.current >= idleTimeoutMs) {
        encerrarSessao('inatividade');
      }
    }, IDLE_CHECK_INTERVAL_MS);

    return () => {
      clearInterval(intervalo);
      ACTIVITY_EVENTS.forEach(evento =>
        window.removeEventListener(evento, registrarAtividade)
      );
    };
  }, [authorized, idleTimeoutMs, encerrarSessao]);

  const handleLogout = () => encerrarSessao();

  if (checkingAuth) {
    return (
      <div className="admin-login-bg">
        <div className="admin-login-card">
          <h1 className="admin-login-title">Verificando acesso...</h1>
        </div>
      </div>
    );
  }

  if (!authorized) {
    return <Navigate to="/tocadochefe" replace />;
  }

  return (
    <div className="admin-container">
      <aside className="admin-sidebar">
        <div className="sidebar-header">Menu</div>
        <nav>
          <ul className="sidebar-nav">
            <li>
              <NavLink
                to="/tocadochefe/painel"
                end
                className={({ isActive }) => isActive ? 'active' : ''}
              >
                <span>📊</span> Dashboard
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/tocadochefe/painel/produtos"
                className={({ isActive }) => isActive ? 'active' : ''}
              >
                <span>🗂</span> Gerenciar Produtos
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/tocadochefe/painel/novo-produto"
                className={({ isActive }) => isActive ? 'active' : ''}
              >
                <span>➕</span> Novo Produto
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/tocadochefe/painel/pedidos"
                className={({ isActive }) => isActive ? 'active' : ''}
              >
                <span>📦</span> Pedidos
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/tocadochefe/painel/clientes"
                className={({ isActive }) => isActive ? 'active' : ''}
              >
                <span>CL</span> Clientes
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/tocadochefe/painel/fornecedores"
                className={({ isActive }) => isActive ? 'active' : ''}
              >
                <span>🏭</span> Fornecedores
              </NavLink>
            </li>
            <li className="sidebar-divider" />
            <li>
              <button onClick={handleLogout}>
                <span>↩</span> Sair
              </button>
            </li>
          </ul>
        </nav>
      </aside>
      <main className="admin-content">
        <Outlet />
      </main>
    </div>
  );
}
